import React, { useEffect, useState } from "react";
import { Button, CloseButton, Modal, Row, Col, Alert } from "react-bootstrap";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import axios from "axios";

interface IProps {
  closeFilterModal: () => void;
  selectedFilterAndValue: any;
  setSelectedFilterAndValue: any;
  handleSubmit: (selectedFilterValues: any) => void;
}

const TaskFilterModal: React.FC<IProps> = ({
  closeFilterModal,
  selectedFilterAndValue,
  setSelectedFilterAndValue,
  handleSubmit,
}) => {
  const [selectedTaskFilters, setSelectedTaskFilters] = useState<string[]>([]);
  const [dateTo, setDateTo] = useState<string>("");
  const [suggestions, setSuggestions] = useState([]);
  const [assigneeSuggestions, setAssigneeSuggestions] = useState([]);
  const [error, setError] = useState<string | null>(null);

  // Store the initial state when modal opens
  const [initialFilters, setInitialFilters] = useState<string[]>([]);
  const [initialFilterValues, setInitialFilterValues] = useState<any>({});
  const [initialDateTo, setInitialDateTo] = useState<string>("");

  const filters = [
    { label: "Id", value: "id" },
    { label: "Assignee", value: "assignee" },
    { label: "Customer email", value: "email_id" },
    { label: "Date", value: "created" },
    { label: "Disposition", value: "disposition_name" },
    { label: "Channel", value: "channel" },
  ];

  // Load filter state from sessionStorage when the component mounts
  useEffect(() => {
    const savedFilters = sessionStorage.getItem("selectedTaskFilters");
    const savedFilterValues = sessionStorage.getItem("selectedFilterAndValue");

    if (savedFilters && savedFilterValues) {
      const parsedFilters = JSON.parse(savedFilters);
      const parsedValues = JSON.parse(savedFilterValues);
      const savedDateTo = parsedValues.created || "";

      setSelectedTaskFilters(parsedFilters);
      setSelectedFilterAndValue(parsedValues);
      setDateTo(savedDateTo);

      // Store initial state
      setInitialFilters(parsedFilters);
      setInitialFilterValues(parsedValues);
      setInitialDateTo(savedDateTo);
    } else {
      const appliedFilters = Object.keys(selectedFilterAndValue).filter(
        (key) => selectedFilterAndValue[key]
      );
      const currentDateTo = selectedFilterAndValue.created || "";

      setSelectedTaskFilters(appliedFilters);
      setDateTo(currentDateTo);

      // Store initial state
      setInitialFilters(appliedFilters);
      setInitialFilterValues(selectedFilterAndValue);
      setInitialDateTo(currentDateTo);
    }
  }, []);

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSelectedTaskFilters((prevSelectedFilters) => {
      let updatedFilters;
      if (prevSelectedFilters.includes(value)) {
        updatedFilters = prevSelectedFilters.filter(
          (filter) => filter !== value
        );
        setSelectedFilterAndValue((prev) => ({
          ...prev,
          [value]: "",
        }));
      } else {
        updatedFilters = [...prevSelectedFilters, value];
      }
      return updatedFilters;
    });
  };

  const handleFilterValueChange = (value: string, filter: string) => {
    setSelectedFilterAndValue((prevFilterValues) => ({
      ...prevFilterValues,
      [filter]: value,
    }));
  };

  const handleDateToChange = (event: any) => {
    const value = event.target.value;
    setDateTo(value);
    setSelectedFilterAndValue((prev) => ({
      ...prev,
      created: value,
    }));
  };

  const resetFilters = () => {
    setSelectedTaskFilters([]);
    const emptyFilters = filters.reduce((acc, filter) => {
      acc[filter.value] = "";
      return acc;
    }, {});
    setSelectedFilterAndValue(emptyFilters);
    setDateTo("");
    setError(null);

    // Update initial state to empty after reset
    setInitialFilters([]);
    setInitialFilterValues(emptyFilters);
    setInitialDateTo("");

    sessionStorage.removeItem("selectedTaskFilters");
    sessionStorage.removeItem("selectedFilterAndValue");
    handleSubmit({});
  };

  // Check if Apply button should be enabled
  const isApplyEnabled = selectedTaskFilters.some((filter) => {
    if (filter === "created") {
      return dateTo && dateTo.trim() !== "";
    }
    const value = selectedFilterAndValue[filter];
    return value && value.toString().trim() !== "";
  });

  // Revert to initial state when closing without applying
  const handleCloseModal = () => {
    // Restore the initial state (what was there when modal opened)
    setSelectedTaskFilters(initialFilters);
    setSelectedFilterAndValue(initialFilterValues);
    setDateTo(initialDateTo);
    closeFilterModal();
  };

  const apiPort = process.env.REACT_APP_API_PROTOCOL;
  const fetchingTenant = window.location.hostname.split(".")[0];

  const searchCustomerEmail = (skey: string) => {
    axios
      .get(
        `${apiPort}://${fetchingTenant}.${process.env.REACT_APP_BASE_URL}/v1/customers/contacts/search-by-email/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth-token")}`,
          },
          params: { email: skey },
        }
      )
      .then((res) => {
        if (res.status === 200) {
          setSuggestions(res.data);
          setError(null);
        }
      })
      .catch(() => {
        setError("Failed to fetch customer email suggestions.");
      });
  };

  const searchAssigneeSuggestions = (skey: string) => {
    axios
      .get(
        `${apiPort}://${fetchingTenant}.${process.env.REACT_APP_BASE_URL}/v1/users/users/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth-token")}`,
          },
          params: { username__icontains: skey },
        }
      )
      .then((res) => {
        if (res.status === 200) {
          setAssigneeSuggestions(res.data);
          setError(null);
        }
      })
      .catch(() => {
        setError("Failed to fetch assignee suggestions.");
      });
  };

  const handleApplyFilters = () => {
    let nextFilters = [...selectedTaskFilters];

    nextFilters = nextFilters.filter((filter) => {
      if (filter === "created") {
        return dateTo && dateTo.trim() !== "";
      }
      const value = selectedFilterAndValue[filter];
      if (!value || value.toString().trim() === "") {
        return false;
      }
      return true;
    });

    const filterValuesToSubmit = nextFilters.reduce((acc, filter) => {
      acc[filter] = selectedFilterAndValue[filter];
      return acc;
    }, {} as any);

    setSelectedTaskFilters(nextFilters);
    setSelectedFilterAndValue(filterValuesToSubmit);

    sessionStorage.setItem("selectedTaskFilters", JSON.stringify(nextFilters));
    sessionStorage.setItem(
      "selectedFilterAndValue",
      JSON.stringify(filterValuesToSubmit)
    );

    handleSubmit(filterValuesToSubmit);
    closeFilterModal();
  };

  return (
    <Modal show={true} onHide={handleCloseModal} centered backdrop="static">
      <Modal.Header>
        <Modal.Title className="fw-bold">Filters</Modal.Title>
        <CloseButton onClick={handleCloseModal} />
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <div className="px-3 py-2">
          {filters.map((filter) => (
            <Row key={filter.value} className="mb-3 align-items-center">
              <Col xs={12} md={4} className="text-nowrap">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedTaskFilters.includes(filter.value)}
                      onChange={handleFilterChange}
                      value={filter.value}
                      color="primary"
                    />
                  }
                  label={filter.label}
                />
              </Col>
              <Col xs={12} md={8}>
                {selectedTaskFilters.includes(filter.value) && (
                  <>
                    {filter.value === "created" ? (
                      <TextField
                        id="date-to"
                        label="Select Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          inputProps: {
                            style: { textTransform: "uppercase" },
                          },
                        }}
                        fullWidth
                        value={dateTo}
                        onChange={handleDateToChange}
                      />
                    ) : filter.value === "email_id" ? (
                      <Autocomplete
                        freeSolo
                        options={suggestions.map((option) => option.email)}
                        value={selectedFilterAndValue[filter.value] || ""}
                        onInputChange={(event, newInputValue) => {
                          searchCustomerEmail(newInputValue);
                        }}
                        onChange={(event, newValue) => {
                          const selectedSuggestion = suggestions.find(
                            (suggestion) => suggestion.email === newValue
                          );

                          setSelectedFilterAndValue((prev) => ({
                            ...prev,
                            email_id: selectedSuggestion
                              ? selectedSuggestion.email
                              : "",
                          }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={filter.label}
                            variant="outlined"
                          />
                        )}
                      />
                    ) : filter.value === "assignee" ? (
                      <Autocomplete
                        freeSolo
                        options={assigneeSuggestions.map(
                          (option) => option.username
                        )}
                        value={selectedFilterAndValue[filter.value] || ""}
                        onInputChange={(event, newInputValue) => {
                          searchAssigneeSuggestions(newInputValue);
                        }}
                        onChange={(event, newValue) => {
                          const selectedSuggestion = assigneeSuggestions.find(
                            (suggestion) => suggestion.username === newValue
                          );

                          setSelectedFilterAndValue((prev) => ({
                            ...prev,
                            assignee: selectedSuggestion
                              ? selectedSuggestion.username
                              : "",
                          }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={filter.label}
                            variant="outlined"
                          />
                        )}
                      />
                    ) : (
                      <TextField
                        label={filter.label}
                        fullWidth
                        value={selectedFilterAndValue[filter.value] || ""}
                        onChange={(e) =>
                          handleFilterValueChange(e.target.value, filter.value)
                        }
                      />
                    )}
                  </>
                )}
              </Col>
            </Row>
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-primary" onClick={resetFilters}>
          Reset
        </Button>
        <Button
          variant="primary"
          onClick={handleApplyFilters}
          disabled={!isApplyEnabled}
        >
          Apply
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TaskFilterModal;